import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ConfigProvider, App as AntdApp } from 'antd'
import zhCN from 'antd/locale/zh_CN'
import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'
import App from './App'
import './styles/globals.css'
import { wechatTheme } from './lib/theme'

dayjs.locale('zh-cn')

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 3,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30_000),
    },
  },
})

// Ant Design 主题 - 微信公众号后台「数据分析」风格
const theme = {
  token: {
    colorPrimary:   wechatTheme.brand.DEFAULT,
    colorSuccess:   wechatTheme.status.success,
    colorProcessing:wechatTheme.status.processing,
    colorWarning:   wechatTheme.status.warning,
    colorError:     wechatTheme.status.error,
    colorInfo:      wechatTheme.status.processing,
    colorLink:      wechatTheme.link.DEFAULT,
    colorLinkHover: wechatTheme.link.hover,
    colorTextBase:  wechatTheme.text.primary,
    colorBgBase:    wechatTheme.bg.body,
    colorBgContainer: wechatTheme.bg.card,
    colorBorder:    wechatTheme.border.DEFAULT,
    colorBorderSecondary: wechatTheme.border.light,
    borderRadius:    wechatTheme.radius.md,
    borderRadiusSM:  wechatTheme.radius.sm,
    borderRadiusLG:  wechatTheme.radius.lg,
    fontFamily:      wechatTheme.fontFamily,
    fontSize:        14,
    // 添加阴影
    boxShadowSecondary: wechatTheme.shadow.md,
    boxShadowTertiary: wechatTheme.shadow.DEFAULT,
    // 动画
    motionDurationFast: '0.15s',
    motionDurationMid: '0.2s',
    motionDurationSlow: '0.3s',
  },
  components: {
    Layout: {
      headerBg: wechatTheme.bg.card,
      headerHeight: 48,
      bodyBg: wechatTheme.bg.body,
      siderBg: wechatTheme.bg.card,
    },
    Menu: {
      itemSelectedBg: wechatTheme.brand.tint,
      itemSelectedColor: wechatTheme.brand.DEFAULT,
      itemHoverBg: wechatTheme.bg.hover,
      itemBorderRadius: wechatTheme.radius.md,
    },
    Card: {
      borderRadiusLG: wechatTheme.radius.lg,
      paddingLG: 20,
      headerBg: wechatTheme.bg.card,
      headerFontSize: 14,
      headerFontWeight: 600,
      headerColor: wechatTheme.text.primary,
      boxShadow: wechatTheme.shadow.DEFAULT,
      boxShadowHover: wechatTheme.shadow.md,
    },
    Button: {
      borderRadius: wechatTheme.radius.md,
      primaryShadow: wechatTheme.shadow.sm,
      defaultShadow: 'none',
      motionDurationFast: '0.15s',
      motionDurationMid: '0.2s',
    },
    Table: {
      headerBg: wechatTheme.bg.section,
      headerColor: wechatTheme.text.secondary,
      headerBorderRadius: wechatTheme.radius.md,
      rowHoverBg: wechatTheme.bg.hover,
      cellPaddingBlock: 10,
      cellPaddingInline: 12,
      borderColor: wechatTheme.border.DEFAULT,
      headerSplitColor: wechatTheme.border.light,
      rowSelectedBg: wechatTheme.brand.tint,
      rowSelectedHoverBg: wechatTheme.brand.tintAlt,
    },
    Tag: {
      borderRadiusSM: wechatTheme.radius.sm,
      defaultBg: wechatTheme.bg.section,
      defaultColor: wechatTheme.text.primary,
    },
    Tabs: {
      itemSelectedColor: wechatTheme.brand.DEFAULT,
      inkBarColor: wechatTheme.brand.DEFAULT,
      itemHoverColor: wechatTheme.text.primary,
      itemActiveColor: wechatTheme.brand.DEFAULT,
      horizontalItemGutter: 24,
      horizontalItemPadding: '10px 0',
    },
    Input: {
      borderRadius: wechatTheme.radius.md,
      hoverBorderColor: wechatTheme.brand.DEFAULT,
      activeBorderColor: wechatTheme.brand.DEFAULT,
      activeShadow: `0 0 0 2px ${wechatTheme.brand.tint}`,
    },
    Segmented: {
      itemSelectedBg: wechatTheme.brand.tint,
      itemSelectedColor: wechatTheme.brand.DEFAULT,
      itemColor: wechatTheme.text.secondary,
      itemHoverColor: wechatTheme.text.primary,
    },
    Alert: {
      borderRadiusLG: wechatTheme.radius.lg,
      messageFontSize: 13,
      descriptionFontSize: 12,
    },
  },
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ConfigProvider locale={zhCN} theme={theme}>
        <AntdApp>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </AntdApp>
      </ConfigProvider>
    </QueryClientProvider>
  </React.StrictMode>,
)
